import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import {
  AgFinancingFile,
  AgFinancingFilesContext,
  AgFinancingFileFolder,
  AgFinancingInput,
  Phase3,
  AgFinancing,
  Phase3Input,
  AgFinancingDocumentType,
  ContractStatus,
  CollateralType,
  Phase2,
  Phase2Input
} from '@indigo-ag/schema';
import { Dictionary } from 'lodash';
import { useMutation } from '@apollo/client';
import { Body, Button, useFlashMessage } from '@indigo-ag/component-module';
import { ExecutionResult } from 'graphql';

import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { DraftData } from 'features/brazil-grower/components/BrazilGrowerFinance/utils/interfaces';
import {
  MultipleFileUploadResult,
  ProgressDictionary,
  uploadFilesToAgFinancing
} from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import { PhaseWrapper } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseWrapper';
import { Draft } from '../../sections/Draft/Draft';
import { CollateralUtil, getAnalysisData, utils } from 'shared';
import { OrderDetail } from 'features/brazil-grower/components/BrazilGrowerFinance/components/OrderDetail/OrderDetail';
import { WarrantyType } from 'features/brazil-grower/components/WarrantyType/WarrantyType';
import { ContractState } from '../../sections/ContractState/ContractState';
import { DocumentsValidationSection } from '../../sections/DocumentsValidationSection/DocumentsValidationSection';
import { NotificationSection } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/NotificationSection/NotificationSection';
import { AmendmentSection } from '../../sections/AmendmentSection/AmendmentSection';
import { SignDigitallySection } from '../../sections/SignDigitallySection/SignDigitallySection';
import { MatriculaAnalysisSection } from '../../sections/Analysis/MatriculaAnalisysSection';

/**
 * TODO Revisit this update logic and try to make it dynamic
 */
export interface ICollateralFormalizationContext {
  updatePhase3: (newPhase3Info: Partial<Phase3Input>, files?: AgFinancingFile[]) => Promise<ExecutionResult<any>>;
  updatePhase2Formalization: (newPhase2Info: Partial<Phase2Input>) => Promise<ExecutionResult<any>>;
  uploadFilesToPhase3: (
    files: File[],
    context: AgFinancingFilesContext,
    documentType?: AgFinancingDocumentType | Dictionary<AgFinancingDocumentType>,
    onUploadProgress?: ((progressDictionary: ProgressDictionary) => void) | undefined
  ) => Promise<MultipleFileUploadResult>;
}

export const CollateralFormalizationContext = React.createContext<ICollateralFormalizationContext>({
  updatePhase3: () => Promise.resolve({}),
  updatePhase2Formalization: () => Promise.resolve({}),
  uploadFilesToPhase3: () => Promise.resolve({ fulfilled: [], rejected: [] })
});

interface CollateralAnalysisProps {
  matriculas: Phase2['matriculas'];
  phase3Info: AgFinancing['phase3'];
  phase3Files: Phase3['files'];
}

export const CollateralFormalization: React.FunctionComponent<CollateralAnalysisProps> = ({
  matriculas,
  phase3Info
}) => {
  /**
   * Finance Process Context
   */
  const { agFinancingId, collateralType, orderInfo, agFinance } = useContext(FinanceProcessContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  /**
   * state
   */
  const [isUpdatingReceivedDocuments, setIsUpdatingReceivedDocuments] = useState(false);

  const getDraftData = (): DraftData => {
    return {
      draftFiles: phase3Info?.files?.filter(f => f.context === AgFinancingFilesContext.CONTRACT_DRAFT_DOCUMENTS) || [],
      ...phase3Info
    };
  };

  const upsertFinancingAgFinancingFnWrapper = (agFinancingData: Partial<AgFinancingInput>) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      ...agFinancingData
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  const updatePhase3 = (newPhase3Info: Partial<Phase3Input>, files?: AgFinancingFile[]) => {
    return upsertFinancingAgFinancingFnWrapper({
      phase3: {
        ...newPhase3Info,
        files
      }
    });
  };

  const updatePhase2Formalization = (newPhase2Info: Partial<Phase2Input>) => {
    return upsertFinancingAgFinancingFnWrapper({
      phase2: {
        ...newPhase2Info
      }
    });
  };

  const uploadFilesToPhase3 = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.PHASE_3
  });

  const isAwaitingPhysicDocs =
    !agFinance?.phase3?.physicalDocumentsReceived &&
    agFinance?.phase3?.contractStatus === ContractStatus.SIGNED_DOCUMENTS_APPROVED;
  const hasSignedFiles = phase3Info?.files?.some(f => f.context === AgFinancingFilesContext.AMENDMENT_SIGN_DOCUMENTS);
  const isPromissoryNote = collateralType === CollateralType.PROMISSORY_NOTE;

  const { addFlashMessage } = useFlashMessage();

  const handlePhysicalDocumentsReceived = () => {
    setIsUpdatingReceivedDocuments(true);
    updatePhase3({ physicalDocumentsReceived: true })
      .then(() =>
        addFlashMessage({
          content: i18next.t('informationUpdated'),
          title: '',
          variant: 'success'
        })
      )
      .catch(() =>
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        })
      )
      .finally(() => setIsUpdatingReceivedDocuments(false));
  };

  return !!phase3Info ? (
    <CollateralFormalizationContext.Provider
      value={{
        updatePhase3,
        updatePhase2Formalization,
        uploadFilesToPhase3
      }}
    >
      <div className="flex">
        <PhaseWrapper
          title={i18next.t('collateralFormalization')}
          subtitleElement={<WarrantyType collateralType={collateralType} />}
          actionElement={
            <>
              {isAwaitingPhysicDocs && (
                <Button
                  variant="secondary"
                  onClick={handlePhysicalDocumentsReceived}
                  busy={isUpdatingReceivedDocuments}
                >
                  {i18next.t('physicalDocumentsReceived')}
                </Button>
              )}
            </>
          }
        >
          {phase3Info?.physicalDocumentsReceived && (
            <NotificationSection
              title={i18next.t('physicalDocumentsReceived')}
              status="success"
              updatedAt={phase3Info?.physicalDocumentsReceivedAt}
            />
          )}
          {phase3Info &&
            phase3Info.contractStatus &&
            ![
              ContractStatus.WAITING_DRAFT,
              ContractStatus.REVIEWING_SIGNED_DOCUMENTS,
              ContractStatus.CREATING_AMENDMENT
            ].includes(phase3Info.contractStatus) && <ContractState phase3Info={phase3Info} />}
          {phase3Info && phase3Info.contractStatus === ContractStatus.WAITING_DIGITAL_SIGNATURE && (
            <SignDigitallySection phase3Info={phase3Info} />
          )}
          {phase3Info?.signedDocumentsReceivedAt && hasSignedFiles ? (
            <DocumentsValidationSection phase3Info={phase3Info} />
          ) : null}
          {phase3Info &&
            (phase3Info.contractStatus === ContractStatus.CREATING_AMENDMENT ||
              phase3Info.amendmentRegistrationNumber) && <AmendmentSection phase3Info={phase3Info} />}
          {phase3Info?.signedDocumentsReceivedAt && !hasSignedFiles ? (
            <DocumentsValidationSection phase3Info={phase3Info} />
          ) : null}
          {!isPromissoryNote && (
            <MatriculaAnalysisSection
              analysisInfo={getAnalysisData(matriculas)}
              includedInFormalization={true}
              canEditMatriculaFormalization={agFinance && !CollateralUtil.isFormalizationComplete(agFinance)}
            />
          )}
          {phase3Info && <Draft data={getDraftData()} />}
        </PhaseWrapper>
        <OrderDetail orderInfo={orderInfo} />
      </div>
    </CollateralFormalizationContext.Provider>
  ) : (
    <Body>{utils.text.toTitleCase(i18next.t('thisProcessHasNotStartedYet'))}</Body>
  );
};
